import IconAdd from 'assets/icons/add.svg';
import IconSubtract from 'assets/icons/subtract.svg';
import cn from 'classnames';
import clamp from 'lodash/clamp';
import debounce from 'lodash/debounce';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DEBOUNCE_DELAY = 500;

export interface Props {
  className?: string;
  debounced?: boolean;
  maxValue?: number;
  minValue?: number;
  suborderPage?: boolean;
  value: number;
  onChange: (newValue: number) => void;
}

const AmountInput: React.FC<Props> = ({
  className,
  debounced,
  maxValue = Infinity,
  minValue = 0,
  suborderPage,
  value,
  onChange,
}) => {
  const [internalValue, setIntervalValue] = useState(value || minValue);

  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (value && value !== internalValue) {
      setIntervalValue(value);
    }
  }, [value]); // `internalValue` shouldn't be in deps

  const onChangeDebounced = useMemo(() => {
    if (!debounced) {
      return (value: number) => onChangeRef.current(value);
    }
    return debounce(
      (value: number) => onChangeRef.current(value),
      DEBOUNCE_DELAY
    );
  }, [debounced]);

  const onIncrement: React.PointerEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.preventDefault();
      const newValue = clamp(internalValue + 1, minValue, maxValue);
      setIntervalValue(newValue);
      onChangeDebounced(newValue);
    },
    [internalValue, minValue, maxValue, onChangeDebounced]
  );

  const onDecrement: React.PointerEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.preventDefault();
      const newValue = clamp(internalValue - 1, minValue, maxValue);
      setIntervalValue(newValue);
      onChangeDebounced(newValue);
    },
    [internalValue, minValue, maxValue, onChangeDebounced]
  );

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      event.stopPropagation();
      const newValue =
        clamp(parseInt(event.target.value, 10), minValue, maxValue) || minValue;
      setIntervalValue(newValue);
      if (newValue !== 0) {
        onChangeDebounced(newValue);
      }
    },
    [minValue, maxValue, setIntervalValue, onChangeDebounced]
  );

  const onInputBlur: React.FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const newValue =
        clamp(parseInt(event.target.value, 10), minValue, maxValue) || minValue;
      setIntervalValue(newValue);
      onChangeRef.current(newValue);
    },
    [minValue, maxValue, setIntervalValue]
  );

  return (
    <div
      className={cn(
        'border-grey-200 flex h-[42px] items-center justify-between rounded-lg border px-3',
        className
      )}
      data-test="amount-section-mobile "
    >
      <button
        className="flex h-8 w-8 items-center justify-center"
        data-test="amount-button-minus"
        disabled={suborderPage}
        onClick={onDecrement}
      >
        <IconSubtract className="text-text-secondary h-4 w-4 grow-0" />
      </button>
      <input
        data-test="amount-input"
        onBlur={onInputBlur}
        onChange={onInputChange}
        value={internalValue}
        className="w-12 text-center outline-hidden"
        id="amount-input"
      />
      <button
        className="flex h-8 w-8 items-center justify-center"
        data-test="amount-button-plus"
        disabled={suborderPage}
        onClick={onIncrement}
      >
        <IconAdd className="text-text-secondary h-4 w-4 grow-0" />
      </button>
    </div>
  );
};

export default AmountInput;
